import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { Loader } from '../common/Loader';
import { BackButton } from '../common/BackButton';
import { BonusProgramInfo } from './BonusProgramInfo';
import { useProfile } from '../../hooks/useProfile';
import { useClinic } from '../../hooks/useClinic';
import { useNavigate } from 'react-router-dom';
import { HelpOutlineOutlined, OpenInNewOutlined } from '@mui/icons-material';

// Функция для склонения "баллов"
const getDeclension = (count: number): string => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'баллов';
  }

  if (lastDigit === 1) {
    return 'балл';
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'балла';
  }

  return 'баллов';
};

export const BalanceList: React.FC = () => {
  const navigate = useNavigate();
  const {
    balance,
    balanceHistory,
    isLoadingBalance,
    balanceError,
  } = useProfile();

  const { clinic } = useClinic();
  const [showBonusInfo, setShowBonusInfo] = useState(false);

  if (isLoadingBalance) {
    return <Loader message="Загрузка баланса..." />;
  }

  if (balanceError) {
    return (
      <Paper sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 3 }}>
        <Typography color="error">Ошибка: {balanceError}</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <BackButton onClick={() => navigate(-1)} />

      {/* Main Balance Panel */}
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '480px', md: '600px' },
          bgcolor: '#007AFF',
          borderRadius: '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 122, 255, 0.3)',
            transform: 'translateY(-2px)',
          },
          aspectRatio: { xs: 'auto', md: '3 / 1.2' },
          minHeight: { xs: '180px', sm: '200px', md: 'auto' },
        }}
      >
        {/* Top Section - "Мой баланс" */}
        <Typography
          sx={{
            fontSize: { xs: 28, sm: 32, md: 36 },
            fontWeight: 600,
            color: '#ffffff',
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            lineHeight: 1.1,
          }}
        >
          Мой баланс
        </Typography>

        {/* Middle Section - Balance with display box */}
        {/* Balance Amount Box */}
        <Box
          sx={{
            minWidth: { xs: '140px', sm: '162px' },
            height: { xs: 44, sm: 50 },
            borderRadius: '22px',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: '#007AFF',
              fontSize: { xs: 18, sm: 20 },
              fontWeight: 500,
              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            {balance} {getDeclension(balance)}
          </Typography>
        </Box>

        {/* Bottom Section - "Подробнее про баллы" */}
        <Button
          onClick={() => setShowBonusInfo(true)}
          sx={{
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            padding: 0,
            minHeight: 'auto',
            justifyContent: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          <HelpOutlineOutlined sx={{ fontSize: '24px', mr: '4px' }} />
          Подробнее про баллы
          <OpenInNewOutlined sx={{ fontSize: '11px', ml: '4px' }} />
        </Button>
      </Box>

      {/* Balance History Section */}
      {balanceHistory.length === 0 ? (
        <Paper sx={{ p: 2, borderRadius: '24px', bgcolor: '#ffffff', width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' }, margin: '0 auto' }}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: 'center', py: 2 }}
          >
            История пуста
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' }, margin: '0 auto' }}>
          {/* Section Title - "История начислений" */}
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#007AFF',
              mb: 1.5,
              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            История начислений
          </Typography>

          {/* History Items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {balanceHistory.slice(0, 10).map((item) => (
              <Paper
                key={item.id}
                sx={{
                  py: 2,
                  borderRadius: '24px',
                  bgcolor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: item.comment && item.comment.length > 30 ? '98px' : '79px',
                  width: '100%',
                  boxSizing: 'border-box',
                  px: 2, // 👈 ДОБАВЛЕНО: добавляет внутренние отступы слева и справа для всего Paper
                }}
              >
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#212121',
                      mb: 0.8,
                      lineHeight: 1.3,
                    }}
                  >
                    {item.comment || (item.transactionType === 'accrual' ? 'Начисление баллов' : 'Списание баллов')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#999999',
                    }}
                  >
                    {new Date(item.createdAt).toLocaleDateString('ru-RU')}
                  </Typography>
                </Box>

                {/* Amount Display Box */}
                <Box
                  sx={{
                    minWidth: '57px',
                    height: '37px',
                    borderRadius: '18.5px',
                    backgroundColor: item.transactionType === 'accrual' ? '#007AFF' : '#212121',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    ml: 1, // 👈 ДОБАВЛЕНО: добавляет отступ слева от текста к кружочку
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontSize: '16px',
                      fontWeight: 600,
                    }}
                  >
                    {item.transactionType === 'accrual' ? '+' : '-'}
                    {Math.abs(item.pointsCount ?? 0)}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Bonus Program Info Modal */}
      <BonusProgramInfo
        open={showBonusInfo}
        onClose={() => setShowBonusInfo(false)}
        clinicData={clinic}
      />
    </Box>
  );
};